import React, { useCallback, useState } from 'react'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { SubmitHandler, useForm } from 'react-hook-form'
import { searchCep } from './api'
import { useApi } from '~/store/store'
import { mutualTheme } from '~/utils/theme'
import { yupResolver } from '@hookform/resolvers/yup'
import { CheckIcon, CloseIcon, SearchIcon } from '~/assets/icons'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { OnboardingRegisterDataActions, OnboardingWizardActions, trackEvent } from '~/service/analytics'
import { CompanyAddress } from '@mutual-services/account-types'
import { HfNumberMaskedInput, HfTextInput } from '~/components'
import { Box, Button, CircularProgress, Grid, InputAdornment, Typography, useMediaQuery } from '@material-ui/core'
import { AccountPendenciesCachedData, CashedDataPayload, MODAL_TYPE } from '../..'
import { transparentize } from 'polished'

export interface AddressModalProps {
  toggleModal: () => void,
  setOpenedModalType: (modalType: MODAL_TYPE) => void,
  modifyCachedData: (key: keyof AccountPendenciesCachedData, payload: CashedDataPayload) => void,
  cachedData: any
}

export type AddressFormValues = CompanyAddress

const ADDRESS_DEFAULT_VALUES = {
  cep: '',
  city: '',
  state: '',
  number: '',
  street: '',
  complement: '',
  neighborhood: ''
}

const validationSchema = yup.object().shape({
  cep: yup.string().required('Campo obrigatório')
    .matches(/(^\d{5}-\d{3}$)|(^\d{8}$)/, 'CEP inválido'),
  city: yup.string().required('Campo obrigatório'),
  state: yup.string().required('Campo obrigatório'),
  number: yup.string().matches(/^\d*\d$/, 'Apenas números são permitidos (demais informações coloque em Complemento)').required('Campo obrigatório'),
  street: yup.string().required('Campo obrigatório'),
  complement: yup.string(),
  neighborhood: yup.string().required('Campo obrigatório')
})

export const AddressModal: React.FC<AddressModalProps> = (props) => {
  const { toggleModal, cachedData, modifyCachedData } = props
  const { address } = cachedData
  const [loading, setLoading] = useState(false)
  const [hasUnifiedCEP, setUnifiedCEP] = useState(false)
  const [pjSetAddress, pjSetAddressState] = useApi('pjSetAddress')
  const matches = useMediaQuery(mutualTheme.breakpoints.down('md'))
  const { _id: accountId } = useAuthAccount()

  const { handleSubmit, control, formState: { errors }, setValue, watch, reset } = useForm<AddressFormValues>({
    defaultValues: address ? address : ADDRESS_DEFAULT_VALUES,
    resolver: yupResolver(validationSchema)
  })

  const cep = watch('cep')

  const { loading: pjSetAddressStateLoading } = pjSetAddressState

  const searchAddress = useCallback(async () => {
    setUnifiedCEP(false)
    const formattedCep = cep.replace('-', '')
    if (formattedCep.length === 8) {
      trackEvent(OnboardingWizardActions.ON_ZIPCODE_SEARCH_CLICK)
      setLoading(true)
      const { data } = await searchCep(cep)
      setLoading(false)

      if (!data || data.erro) {
        reset()
        toast.error('CEP digitado é inválido ou está incorreto', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 3000,
          closeButton: false
        })
        return
      }

      if (data.logradouro === '') {
        setUnifiedCEP(true)
      }

      const { localidade, bairro, logradouro, uf } = data
      setValue('state', uf)
      setValue('city', localidade)
      setValue('street', logradouro)
      setValue('neighborhood', bairro)
      setValue('number', '')
      setValue('complement', '')
    }
  }, [setValue, cep, trackEvent])

  const onSubmit: SubmitHandler<AddressFormValues> = async (address) => {
    const response = await pjSetAddress({ accountId, address })

    if (response.ok) {
      modifyCachedData('address', address)
      trackEvent(OnboardingRegisterDataActions.FULL_COMERCIAL_ADDRESS)
      toggleModal()
    } else {
      toast.error('Erro ao modificar endereço', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
        closeButton: false
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', marginBottom: 4 }}>
          <Typography sx={{ fontWeight: 'fontWeightBold' }} variant={matches ? 'airbnbH3' : 'airbnbH2'}>
            Endereço comercial
          </Typography>
          <Typography sx={{ color: 'textGray1.main' }} variant={matches ? 'airbnbBody2' : 'airbnbBody1'}>
            Informe o endereço comercial ou legalmente registrado da sua empresa.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Grid container spacing={3} direction={matches ? 'column' : 'row'}>
            <Grid item md={6}>
              <HfNumberMaskedInput
                name='cep'
                label='CEP'
                formatted
                shrink
                mask='#####-###'
                control={control}
                error={errors.cep?.message}
                errorMessage={errors.cep?.message}
                placeholder='Digite o CEP e clique em buscar'
                endAdornment={(
                  <InputAdornment position='end'>
                    <Button
                      variant='contained'
                      color='secondary'
                      size='large'
                      disabled={loading}
                      startIcon={<SearchIcon />}
                      onClick={searchAddress}
                      sx={{
                        color: 'secondary.main',
                        backgroundColor: 'badgeBorderColor.info.main',
                        height: 40,
                        width: 94,
                        '&:hover': {
                          backgroundColor: transparentize(0.20, mutualTheme.palette.badgeBorderColor.info.main)
                        }
                      }}
                    >
                      {(loading)
                        ? (
                          <CircularProgress color='inherit' size={20} />
                        )
                        : (
                          <Typography variant='airbnbBody2' sx={{ fontWeight: 600 }}>Buscar</Typography>
                        )}
                    </Button>
                  </InputAdornment>
                )}
              />
            </Grid>
            <Box width='100%' />
            <Grid item md={6}>
              <HfTextInput
                shrink
                name='street'
                label='Endereço'
                control={control}
                readOnly={!hasUnifiedCEP}
                error={errors.street?.message}
                errorMessage={errors.street?.message}
                placeholder='Digite o endereço comercial'
              />
            </Grid>
            <Grid item md={6}>
              <HfTextInput
                shrink
                name='number'
                label='Número'
                control={control}
                error={errors.number?.message}
                errorMessage={errors.number?.message}
                placeholder='Digite o número'
              />
            </Grid>
            <Grid item md={6}>
              <HfTextInput
                shrink
                name='complement'
                control={control}
                label='Complemento'
                error={errors.complement?.message}
                errorMessage={errors.complement?.message}
                placeholder={'Digite o complemento, se houver'}
              />
            </Grid>
            <Grid item md={6}>
              <HfTextInput
                shrink
                label='Bairro'
                control={control}
                name='neighborhood'
                readOnly={!hasUnifiedCEP}
                error={errors.neighborhood?.message}
                errorMessage={errors.neighborhood?.message}
                placeholder='Digite o bairro'
              />
            </Grid>

            <Grid item md={6}>
              <HfTextInput
                shrink
                readOnly
                name='city'
                label='Cidade'
                control={control}
                error={errors.city?.message}
                errorMessage={errors.city?.message}
                placeholder='Digite a cidade'
              />
            </Grid>
            <Grid item md={6}>
              <HfTextInput
                shrink
                readOnly
                name='state'
                label='Estado'
                control={control}
                placeholder='Selecione'
                error={errors.state?.message}
                errorMessage={errors.state?.message}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: [
            'column-reverse',
            'column-reverse',
            'row',
            'row',
            'row'
          ],
          justifyContent: 'space-between',
          marginTop: 3
        }}>
          <Button
            variant='contained'
            color='info'
            onClick={toggleModal}
            size='large'
            startIcon={<CloseIcon htmlColor={mutualTheme.palette.secondary.main} />}
            sx={{
              marginTop: [2, 2, 0, 0, 0],
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold',
              color: 'secondary.main',
              ':hover': {
                bgcolor: 'rgba(54,141,247,0.2)'
              }
            }}
          >
            Cancelar
          </Button>

          <Button
            type='submit'
            variant='contained'
            color='primary'
            size='large'
            disabled={pjSetAddressStateLoading}
            startIcon={pjSetAddressStateLoading
              ? <CircularProgress color='inherit' size={20} />
              : <CheckIcon htmlColor={mutualTheme.palette.common.white} />
            }
            sx={{
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold'
            }}
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </form>
  )
}
